import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Loader2, Video, Phone, Check, ArrowLeft, PartyPopper } from "lucide-react";

const pyWeekday = (d) => (d.getDay() + 6) % 7; // JS Sun=0 → Python Mon=0
const ymd = (d) => format(d, "yyyy-MM-dd");

export default function ConsultationBooking() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState("");
  const [meetingType, setMeetingType] = useState("Video");
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/consultations/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!date) return;
    setSlotsLoading(true); setTime("");
    api.get(`/consultations/slots?date=${ymd(date)}`)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [date]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const disabledDay = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (cfg && !cfg.working_days.includes(pyWeekday(d))) return true;
    if (cfg && cfg.blackout_dates.includes(ymd(d))) return true;
    return false;
  };

  const canSubmit = date && time && form.name.trim() && /\S+@\S+\.\S+/.test(form.email) &&
    (meetingType !== "Phone" || form.phone.trim());

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/consultations", {
        ...form, meeting_type: meetingType, date: ymd(date), time,
      });
      setDone(data.booking);
    } catch (e) {
      toast.error(formatApiError(e));
      if (String(e?.response?.status) === "409") {
        api.get(`/consultations/slots?date=${ymd(date)}`).then((r) => setSlots(r.data.slots || []));
        setTime("");
      }
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="booking-success">
        <div className="w-16 h-16 mx-auto border border-gold flex items-center justify-center text-gold mb-8"><PartyPopper size={28} /></div>
        <h1 className="font-heading text-4xl text-white mb-4">You're booked, {done.name.split(" ")[0]}!</h1>
        <p className="text-gray-400 text-lg mb-2">Your free consultation is confirmed for</p>
        <p className="text-gold font-heading text-2xl mb-6">{done.date} at {done.time_label} <span className="text-gray-500 text-base">({done.timezone_label})</span></p>
        <p className="text-gray-400 mb-8">
          {done.meeting_type === "Video"
            ? "We'll email you a video meeting link before your call."
            : `We'll call you at ${done.phone} at that time.`} A confirmation is on its way to <span className="text-white">{done.email}</span>.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          {user ? (
            <Link to="/portal" className="bg-gold text-navy px-8 py-4 font-semibold hover:bg-gold-light transition-colors">Go to My Portal</Link>
          ) : (
            <Link to="/start" className="bg-gold text-navy px-8 py-4 font-semibold hover:bg-gold-light transition-colors">Take the Free Assessment</Link>
          )}
          <Link to="/" className="border border-white/25 text-white px-8 py-4 hover:bg-white/5 transition-colors">Back to Home</Link>
        </div>
        {!user && (
          <p className="text-gray-500 text-sm mt-6">Want a head start before our call? The free assessment takes about 3 minutes and gives us a tailored plan to review together.</p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-16 md:py-24" data-testid="consultation-booking">
      <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3 flex items-center gap-2"><CalendarDays size={14} /> Free Consultation</div>
      <h1 className="font-heading text-4xl md:text-5xl text-white mb-3">Book your free consultation</h1>
      <p className="text-gray-400 text-lg mb-10 max-w-2xl">Pick a time that works for you. It's a no-pressure conversation about your goals — {cfg ? `${cfg.slot_minutes} minutes, ${cfg.timezone_label}.` : "30 minutes."}</p>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Calendar */}
        <div className="glass p-4 md:p-6">
          <div className="text-sm text-gray-400 mb-3">1. Choose a date</div>
          <Calendar mode="single" selected={date} onSelect={setDate} disabled={disabledDay}
            className="text-white" data-testid="booking-calendar" />
        </div>

        {/* Slots + form */}
        <div className="glass p-4 md:p-6">
          {!date ? (
            <div className="h-full flex items-center justify-center text-gray-500 text-sm py-16">Select a date to see available times.</div>
          ) : (
            <>
              <div className="text-sm text-gray-400 mb-3">2. Pick a time <span className="text-gray-600">· {format(date, "EEE, MMM d")}</span></div>
              {slotsLoading ? (
                <div className="flex items-center gap-2 text-gray-400 py-6"><Loader2 className="animate-spin" size={16} /> Loading times…</div>
              ) : slots.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">No times available that day — please pick another date.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2 mb-6" data-testid="booking-slots">
                  {slots.map((s) => (
                    <button key={s.time} disabled={!s.available} onClick={() => setTime(s.time)} data-testid={`slot-${s.time}`}
                      className={`px-2 py-2 text-sm border transition-colors ${time === s.time ? "bg-gold text-navy border-gold font-semibold" : "border-white/15 text-gray-300 hover:border-gold/50"} ${!s.available ? "opacity-25 cursor-not-allowed line-through" : ""}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              )}

              {time && (
                <div className="space-y-4" data-testid="booking-form">
                  <div>
                    <div className="text-sm text-gray-400 mb-2">3. How should we meet?</div>
                    <div className="flex gap-2">
                      {(cfg?.meeting_types || ["Video", "Phone"]).map((mt) => (
                        <button key={mt} onClick={() => setMeetingType(mt)} data-testid={`meeting-${mt}`}
                          className={`flex items-center gap-2 px-4 py-2 text-sm border transition-colors ${meetingType === mt ? "bg-gold text-navy border-gold font-semibold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>
                          {mt === "Video" ? <Video size={15} /> : <Phone size={15} />} {mt}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">{meetingType === "Video" ? "We'll email you a meeting link before the call." : "We'll call the number you provide."}</p>
                  </div>
                  <Field label="Full name *" value={form.name} onChange={(v) => set("name", v)} testid="booking-name" />
                  <Field label="Email *" value={form.email} onChange={(v) => set("email", v)} testid="booking-email" type="email" />
                  <Field label={meetingType === "Phone" ? "Phone *" : "Phone"} value={form.phone} onChange={(v) => set("phone", v)} testid="booking-phone" />
                  <Field label="Business / organization" value={form.business_name} onChange={(v) => set("business_name", v)} testid="booking-business" />
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Anything you'd like us to know?</label>
                    <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="booking-notes"
                      className="w-full bg-navy border border-white/20 text-white p-3 text-sm focus:border-gold focus:outline-none" placeholder="Optional" />
                  </div>
                  <button onClick={submit} disabled={!canSubmit || submitting} data-testid="booking-submit"
                    className="w-full bg-gold text-navy py-3.5 font-semibold hover:bg-gold-light transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Confirm my free consultation
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Link to="/start" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gold mt-10"><ArrowLeft size={14} /> Back to assessment</Link>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <label className="text-sm text-gray-400 mb-1.5 block">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-navy border border-white/20 text-white p-3 text-sm focus:border-gold focus:outline-none" />
  </div>
);
